import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Power, PowerOff, Info, AlertTriangle, Check, Copy } from 'lucide-react';
import { appNutzer, type AppNutzer, type Rechtefeld } from '../api';

/**
 * App-Zugänge einer Instanz — wer sich hier anmelden kann und womit.
 *
 * ══ NICHT ZU VERWECHSELN MIT DEN PORTALKONTEN ══════════════════════════════
 *
 * `CustomerUsersPanel` zeigt die **Portalkonten** des Kunden: Verträge,
 * Rechnungen, Kündigung — eine kaufmännische Identität. Hier geht es um
 * **Arbeitszugänge** in der Anwendung. Derselbe Mensch kann beides sein, keines
 * oder nur eines, und die beiden sind nicht verknüpft.
 *
 * ══ DER PC SPEICHERT NICHTS DAVON ══════════════════════════════════════════
 *
 * Jede Liste ist frisch aus der Instanz gelesen. Es gibt kein zentrales
 * Verzeichnis — Kunden betreiben beide Anwendungen auch im eigenen Intranet,
 * ohne Verbindung hierher, und ein gespiegeltes Original wäre dort falsch.
 *
 * Folge, die man sieht: die Ansicht ist so schnell wie die Instanz und leer,
 * wenn sie steht. Das ist ehrlicher als eine Kopie, die dann noch etwas zeigt.
 *
 * ══ RECHTE STEHEN IN DER SPRACHE DER APP ═══════════════════════════════════
 *
 * Die Felder kommen als Liste vom Server — Titel, Art, Auswahl, Wert. Diese
 * Komponente weiß nicht, was ein Mandant oder eine Rollengruppe ist, und das
 * ist Absicht: bekommt ein Produkt ein Feld dazu, erscheint es hier, ohne dass
 * jemand diese Datei anfasst. Nichts wird zwischen den Apps übersetzt.
 */

const K = {
  karte: 'rounded-lg border border-slate-200 bg-surface',
  knopf: 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium '
    + 'border border-slate-300 hover:bg-slate-50 '
    + 'disabled:opacity-50 disabled:cursor-not-allowed',
  feld: 'w-full rounded-md border border-slate-300 '
    + 'bg-surface px-2.5 py-1.5 text-sm',
  th: 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500',
  td: 'px-3 py-2 text-sm align-top',
};

function Hinweis({ art, children }: { art: 'info' | 'warn'; children: React.ReactNode }) {
  const Symbol = art === 'warn' ? AlertTriangle : Info;
  const farbe = art === 'warn'
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-slate-200 bg-slate-50 text-slate-600';
  return (
    <div className={`flex gap-2 rounded-md border px-3 py-2 text-sm ${farbe}`}>
      <Symbol className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

/** Ein Rechtefeld, generisch dargestellt. */
function RechteFeldZeile({
  feld, gesperrt, onSetzen,
}: {
  feld: Rechtefeld;
  gesperrt: boolean;
  onSetzen: (wert: string | string[] | null) => void;
}) {
  if (feld.art === 'mehrfach') {
    const gewaehlt = new Set(Array.isArray(feld.wert) ? feld.wert : []);
    return (
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {feld.titel}
        </div>
        {feld.optionen.length === 0 && (
          <div className="text-sm text-slate-500">
            Die Instanz meldet hier keine Auswahl.
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {feld.optionen.map(o => (
            <label key={o.wert} className="inline-flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                disabled={gesperrt}
                checked={gewaehlt.has(o.wert)}
                onChange={(e) => {
                  // Der Endpunkt nimmt die Liste als GANZES — hier wird sie
                  // gebaut und nicht als Differenz geschickt.
                  const neu = new Set(gewaehlt);
                  if (e.target.checked) neu.add(o.wert); else neu.delete(o.wert);
                  onSetzen([...neu]);
                }}
              />
              {o.text}
            </label>
          ))}
        </div>
      </div>
    );
  }

  const wert = typeof feld.wert === 'string' ? feld.wert : '';
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {feld.titel}
      </span>
      <select
        className={K.feld}
        disabled={gesperrt}
        value={wert}
        onChange={(e) => onSetzen(e.target.value || null)}
      >
        <option value="">— nicht gesetzt —</option>
        {feld.optionen.map(o => (
          <option key={o.wert} value={o.wert}>{o.text}</option>
        ))}
      </select>
    </label>
  );
}

/** Die aufgeklappte Rechteansicht eines Nutzers. */
function Rechte({ instanceId, nutzer }: { instanceId: string; nutzer: AppNutzer }) {
  const qc = useQueryClient();
  const [fehler, setFehler] = useState<string | null>(null);

  const abfrage = useQuery({
    queryKey: ['app-nutzer-rechte', instanceId, nutzer.id],
    queryFn: () => appNutzer.rechte(instanceId, nutzer.id),
  });

  const setzen = useMutation({
    mutationFn: ({ schluessel, wert }: { schluessel: string; wert: string | string[] | null }) =>
      appNutzer.setzeRecht(instanceId, nutzer.id, schluessel, wert),
    onSuccess: (daten) => {
      setFehler(null);
      qc.setQueryData(['app-nutzer-rechte', instanceId, nutzer.id], daten);
      qc.invalidateQueries({ queryKey: ['app-nutzer', instanceId] });
    },
    onError: (e: unknown) => setFehler(meldungAus(e)),
  });

  if (abfrage.isLoading) return <div className="p-3 text-sm text-slate-500">…</div>;
  if (abfrage.isError) {
    return <div className="p-3"><Hinweis art="warn">{meldungAus(abfrage.error)}</Hinweis></div>;
  }

  const felder = abfrage.data?.felder ?? [];
  return (
    <div className="space-y-3 border-l-2 border-slate-200 p-3 pl-4">
      {fehler && <Hinweis art="warn">{fehler}</Hinweis>}
      {nutzer.geschuetzt && (
        <Hinweis art="info">
          Geschütztes Konto — Rechte lassen sich hier nicht ändern. Das Betreiberkonto ist
          zugleich der Zugang, mit dem der PC sich an dieser Instanz anmeldet.
        </Hinweis>
      )}
      {felder.map(f => (
        <RechteFeldZeile
          key={f.schluessel}
          feld={f}
          gesperrt={setzen.isPending || !!nutzer.geschuetzt}
          onSetzen={(wert) => setzen.mutate({ schluessel: f.schluessel, wert })}
        />
      ))}
      <p className="text-xs text-slate-500">
        Gilt nur in dieser Anwendung. Zugänge in anderen Apps desselben Kunden bleiben
        unberührt — die Rechtemodelle sind voneinander entkoppelt.
      </p>
    </div>
  );
}

function meldungAus(e: unknown): string {
  const d = (e as { response?: { data?: { meldung?: string; message?: string } } })?.response?.data;
  return d?.meldung ?? d?.message ?? (e as Error)?.message ?? 'Unbekannter Fehler.';
}

export default function AppNutzerPanel({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const [offen, setOffen] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [neu, setNeu] = useState({ email: '', anrede: '', vorname: '', nachname: '', rolle: '' });
  const [passwort, setPasswort] = useState<{ email: string; wert: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ['app-nutzer', instanceId],
    queryFn: () => appNutzer.liste(instanceId),
    retry: false,
  });

  const rollen = useQuery({
    queryKey: ['app-nutzer-rollen', instanceId],
    queryFn: () => appNutzer.rollen(instanceId),
    retry: false,
  });

  const anlegen = useMutation({
    mutationFn: () => appNutzer.anlegen(instanceId, {
      email: neu.email.trim(),
      nachname: neu.nachname.trim(),
      vorname: neu.vorname.trim() || undefined,
      anrede: neu.anrede || undefined,
      rolle: neu.rolle || undefined,
    }),
    onSuccess: (daten) => {
      setFehler(null);
      setNeuOffen(false);
      setNeu({ email: '', anrede: '', vorname: '', nachname: '', rolle: '' });
      // Das Passwort kommt genau einmal. Danach ist es aus dem PC nicht mehr zu
      // holen — er speichert es nicht, und was er nicht führt, verliert er nicht.
      if (daten.passwort) setPasswort({ email: daten.nutzer.email, wert: daten.passwort });
      qc.invalidateQueries({ queryKey: ['app-nutzer', instanceId] });
    },
    onError: (e: unknown) => setFehler(meldungAus(e)),
  });

  const schalten = useMutation({
    mutationFn: ({ id, an }: { id: string; an: boolean }) =>
      appNutzer.schalten(instanceId, an, { id }),
    onSuccess: () => {
      setFehler(null);
      qc.invalidateQueries({ queryKey: ['app-nutzer', instanceId] });
    },
    onError: (e: unknown) => setFehler(meldungAus(e)),
  });

  if (liste.isError) {
    return (
      <div className="space-y-3">
        <Hinweis art="warn">{meldungAus(liste.error)}</Hinweis>
        <p className="text-sm text-slate-500">
          Der PC liest die Zugänge direkt aus der laufenden Instanz. Steht sie, gibt es
          hier nichts zu zeigen — eine zwischengespeicherte Liste wäre genau dann falsch,
          wenn es darauf ankommt.
        </p>
      </div>
    );
  }

  const nutzer = liste.data?.nutzer ?? [];

  return (
    <div className="space-y-4">
      <Hinweis art="info">
        <strong>Arbeitszugänge in dieser Anwendung</strong> — nicht die Portalkonten des
        Kunden (Verträge, Rechnungen). Das sind zwei getrennte Welten; derselbe Mensch
        kann in einer, in beiden oder in keiner stehen.
      </Hinweis>

      {fehler && <Hinweis art="warn">{fehler}</Hinweis>}

      {passwort && (
        <div className={`${K.karte} p-3`}>
          <div className="mb-1 text-sm font-semibold">
            Erstpasswort für {passwort.email}
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-slate-100 px-2 py-1 text-sm">
              {passwort.wert}
            </code>
            <button
              type="button"
              className={K.knopf}
              onClick={() => navigator.clipboard?.writeText(passwort.wert)}
            >
              <Copy className="h-4 w-4" aria-hidden /> Kopieren
            </button>
            <button type="button" className={K.knopf} onClick={() => setPasswort(null)}>
              <Check className="h-4 w-4" aria-hidden /> Notiert
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Wird <strong>einmal</strong> angezeigt. Der PC speichert es nicht — wer es
            verpasst, schickt stattdessen einen Zugangslink über „Zugänge".
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {liste.isFetching ? 'liest aus der Instanz …' : `${nutzer.length} Zugänge`}
          {liste.data?.produkt && ` · ${liste.data.produkt}`}
        </div>
        <button type="button" className={K.knopf} onClick={() => setNeuOffen(v => !v)}>
          <UserPlus className="h-4 w-4" aria-hidden /> Zugang einrichten
        </button>
      </div>

      {neuOffen && (
        <div className={`${K.karte} space-y-3 p-3`}>
          {/*
            Drei Namensfelder statt einem. bryve führt in seinem Feld `name` den
            Nachnamen und hat daneben Vorname und Anrede; ein voller Name in
            einem Feld landete dort geschlossen im Nachnamen — „Nachname: Robert
            Görlich", Vorname leer. Aufgeteilt wird deshalb hier, wo jemand die
            Person kennt, und nicht später durch Raten am Leerzeichen.
          */}
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Anrede
              </span>
              <select
                className={K.feld} value={neu.anrede}
                onChange={(e) => setNeu({ ...neu, anrede: e.target.value })}
              >
                <option value="">— keine —</option>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
                <option value="Divers">Divers</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Vorname
              </span>
              <input
                className={K.feld} value={neu.vorname}
                onChange={(e) => setNeu({ ...neu, vorname: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Nachname
              </span>
              <input
                className={K.feld} value={neu.nachname}
                onChange={(e) => setNeu({ ...neu, nachname: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                E-Mail
              </span>
              <input
                className={K.feld} type="email" value={neu.email}
                onChange={(e) => setNeu({ ...neu, email: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Startrolle
              </span>
              <select
                className={K.feld} value={neu.rolle}
                onChange={(e) => setNeu({ ...neu, rolle: e.target.value })}
              >
                <option value="">{rollen.data?.standard ?? '— Vorgabe —'}</option>
                {(rollen.data?.rollen ?? []).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Gibt es die Adresse schon, wird <strong>nichts</strong> angelegt und kein
            Passwort zurückgesetzt. Feinere Rechte werden danach je Zugang gesetzt.
          </p>
          <button
            type="button"
            className={`${K.knopf} font-semibold`}
            disabled={!neu.email.trim() || !neu.nachname.trim() || anlegen.isPending}
            onClick={() => anlegen.mutate()}
          >
            Anlegen
          </button>
        </div>
      )}

      <div className={`${K.karte} overflow-hidden`}>
        <table className="w-full">
          <thead className="border-b border-slate-200">
            <tr>
              <th className={K.th}>Adresse</th>
              <th className={K.th}>Name</th>
              <th className={K.th}>Rolle</th>
              <th className={K.th}>Stand</th>
              <th className={K.th} />
            </tr>
          </thead>
          <tbody>
            {nutzer.map(n => (
              <React.Fragment key={n.id || n.email}>
                <tr className="border-b border-slate-100">
                  <td className={K.td}>
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() => setOffen(offen === n.id ? null : n.id)}
                    >
                      {n.email}
                    </button>
                  </td>
                  <td className={K.td}>{n.name ?? '—'}</td>
                  <td className={K.td}>{n.rolle ?? '—'}</td>
                  <td className={K.td}>
                    {n.aktiv
                      ? <span className="text-emerald-700">aktiv</span>
                      : (
                        <span className="text-slate-500">
                          abgeschaltet
                          {n.abgeschaltetAm
                            && ` · ${new Date(n.abgeschaltetAm).toLocaleDateString('de-DE')}`}
                        </span>
                      )}
                  </td>
                  <td className={`${K.td} text-right`}>
                    {/* Geschuetzte Konten bekommen keinen Knopf. Einen anzubieten,
                        der in eine Ablehnung laeuft, ist schlechter als keiner —
                        und der Grund gehoert daneben, nicht in eine Meldung
                        danach. */}
                    {n.geschuetzt ? (
                      <span className="text-xs text-slate-500">geschütztes Konto</span>
                    ) : (
                      <button
                        type="button"
                        className={K.knopf}
                        disabled={schalten.isPending}
                        onClick={() => schalten.mutate({ id: n.id, an: !n.aktiv })}
                      >
                        {n.aktiv
                          ? <><PowerOff className="h-4 w-4" aria-hidden /> Abschalten</>
                          : <><Power className="h-4 w-4" aria-hidden /> Einschalten</>}
                      </button>
                    )}
                  </td>
                </tr>
                {offen === n.id && (
                  <tr>
                    <td colSpan={5} className="bg-slate-50 p-0">
                      <Rechte instanceId={instanceId} nutzer={n} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {nutzer.length === 0 && !liste.isFetching && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-500">
                  Die Instanz meldet keine Zugänge.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Abschalten nimmt den Zugang und lässt den Datensatz stehen — Aufgaben, Zeiten und
        Kommentare bleiben der Person zugeordnet. Wirkliches Löschen (Art. 17 DSGVO) gibt
        es hier bewusst nicht; das geschieht in der Anwendung selbst.
      </p>
    </div>
  );
}
